import { useMutation, useQueryClient } from "@tanstack/react-query"
import * as React from "react"

import {
  clearAuthSession,
  readAuthSession,
  subscribeAuthSession,
  writeAuthSession,
  type AuthSession,
  type StoredUser,
} from "@/lib/auth-token"
import { ApiError } from "@/lib/errors"
import { http } from "@/lib/http"

import { Role, toRole } from "@/lib/roles"

import {
  LoginError,
  type LoginInput,
  type Session,
  type User,
  type UserId,
} from "./auth-data"

/**
 * Auth mutations, current-user hook, and session persistence.
 *
 * The page consumes `useLoginMutation()` and knows nothing about the transport.
 * On success the wire response is normalized into an `AuthSession` and written
 * to the storage layer; `useCurrentUser` reads it back via `useSyncExternalStore`
 * so a logout in one tab bounces every open tab to `/login` on the next render.
 *
 * The refresh token, when the reviewer opts into Remember-me, is stored
 * alongside the access token. The refresh loop in `lib/http.ts` uses it to
 * rotate an expired access token without asking the reviewer to sign in again.
 */

// Wire types mirror pyxis/api schemas/auth.py.
interface UserOutWire {
  id: number
  email: string
  full_name: string | null
  role: string
  is_active: boolean
  created_at: string
  invited_at?: string | null
  accepted_at?: string | null
}

interface TokenResponseWire {
  access_token: string
  token_type: string
  expires_in: number
  user: UserOutWire
  refresh_token: string | null
  refresh_expires_in: number | null
}

interface LoginRequestWire {
  email: string
  password: string
  remember_me: boolean
}

interface RefreshRequestWire {
  refresh_token: string
}

function toUser(wire: UserOutWire): User {
  return {
    id: String(wire.id) as UserId,
    email: wire.email,
    name: wire.full_name,
    // Unknown role → viewer (safest non-client fallback for a staff console user record).
    role: toRole(wire.role) ?? Role.VIEWER,
  }
}

function toStored(user: User): StoredUser {
  return { id: user.id, email: user.email, name: user.name, role: user.role }
}

function fromStored(stored: StoredUser): User {
  return {
    id: stored.id as UserId,
    email: stored.email,
    name: stored.name,
    role: toRole(stored.role) ?? Role.VIEWER,
  }
}

/**
 * Map anything the login endpoint can throw into the discriminated
 * `LoginError` the page renders. The page's banner already switches on
 * `kind`, so all inference belongs here — never in the UI.
 */
function toLoginError(cause: unknown): LoginError {
  if (cause instanceof ApiError) {
    if (cause.status === 401) {
      return new LoginError("invalid_credentials", "Email or password is incorrect.")
    }
    if (cause.status === 429) {
      return new LoginError(
        "rate_limited",
        "Too many attempts. Try again shortly.",
        cause.retryAfterSeconds,
      )
    }
    // The backend does not (yet) return a distinct `account_locked` code.
    // When it does, key off `cause.code` here. Until then everything else
    // 4xx-shaped is a generic invalid.
    if (cause.status >= 400 && cause.status < 500) {
      return new LoginError("invalid_credentials", "Email or password is incorrect.")
    }
    if (cause.isServer) {
      return new LoginError("server", "Server error. Please try again in a moment.")
    }
    if (cause.status === 0) {
      return new LoginError("network", "We couldn't reach the server.")
    }
  }
  return new LoginError("unknown", "Something went wrong. Please try again.")
}

async function loginRequest(input: LoginInput): Promise<Session> {
  const wire = await http.post<TokenResponseWire, LoginRequestWire>("/api/v1/auth/login", {
    email: input.email.trim(),
    password: input.password,
    remember_me: input.rememberMe,
  })
  const user = toUser(wire.user)
  const now = Date.now()
  const expiresAtMs = now + wire.expires_in * 1000
  const refreshExpiresAtMs =
    wire.refresh_expires_in !== null ? now + wire.refresh_expires_in * 1000 : null
  writeAuthSession({
    token: wire.access_token,
    user: toStored(user),
    expiresAt: expiresAtMs,
    refreshToken: wire.refresh_token,
    refreshExpiresAt: refreshExpiresAtMs,
    rememberMe: input.rememberMe,
  } satisfies AuthSession)
  return { user, expiresAt: new Date(expiresAtMs).toISOString() }
}

export interface LoginMutation {
  mutateAsync: (input: LoginInput) => Promise<Session>
  isPending: boolean
  error: LoginError | null
  reset: () => void
}

export function useLoginMutation(): LoginMutation {
  const queryClient = useQueryClient()
  const mutation = useMutation<Session, LoginError, LoginInput>({
    mutationFn: async (input) => {
      try {
        return await loginRequest(input)
      } catch (cause) {
        throw toLoginError(cause)
      }
    },
    onSuccess: () => {
      // A stale cache from a previous session must not bleed into this one.
      queryClient.clear()
    },
  })

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  }
}

export function useLogoutMutation(): {
  logout: () => void
  isPending: boolean
} {
  const queryClient = useQueryClient()
  const [isPending, setIsPending] = React.useState(false)

  const logout = React.useCallback(() => {
    setIsPending(true)
    const session = readAuthSession()
    // Fire-and-forget: the local wipe below is what the reviewer sees;
    // a failed server ping must not strand them "signed in" locally. The
    // refresh token in the body lets the backend revoke the family so a
    // stolen copy cannot rotate again.
    const body: RefreshRequestWire | undefined = session?.refreshToken
      ? { refresh_token: session.refreshToken }
      : undefined
    void http.post("/api/v1/auth/logout", body).catch(() => {})
    clearAuthSession()
    queryClient.clear()
    setIsPending(false)
  }, [queryClient])

  return { logout, isPending }
}

export interface AcceptInviteInput {
  token: string
  newPassword: string
  fullName: string | null
}

interface AcceptInviteWire {
  token: string
  new_password: string
  full_name?: string | null
}

export type AcceptInviteErrorKind = "invalid_or_expired" | "network" | "server" | "unknown"

export class AcceptInviteError extends Error {
  readonly kind: AcceptInviteErrorKind
  constructor(kind: AcceptInviteErrorKind, message: string) {
    super(message)
    this.name = "AcceptInviteError"
    this.kind = kind
  }
}

function toAcceptInviteError(cause: unknown): AcceptInviteError {
  if (cause instanceof ApiError) {
    if (cause.status === 400) {
      return new AcceptInviteError(
        "invalid_or_expired",
        "This invitation is invalid or has expired.",
      )
    }
    if (cause.isServer) return new AcceptInviteError("server", "Server error. Try again shortly.")
    if (cause.status === 0) return new AcceptInviteError("network", "We couldn't reach the server.")
  }
  return new AcceptInviteError("unknown", "Something went wrong. Please try again.")
}

async function acceptInviteRequest(input: AcceptInviteInput): Promise<Session> {
  const body: AcceptInviteWire = {
    token: input.token,
    new_password: input.newPassword,
  }
  if (input.fullName && input.fullName.trim()) body.full_name = input.fullName.trim()
  const wire = await http.post<TokenResponseWire, AcceptInviteWire>(
    "/api/v1/auth/accept-invite",
    body,
  )
  const user = toUser(wire.user)
  const now = Date.now()
  const expiresAtMs = now + wire.expires_in * 1000
  writeAuthSession({
    token: wire.access_token,
    user: toStored(user),
    expiresAt: expiresAtMs,
    refreshToken: wire.refresh_token,
    refreshExpiresAt:
      wire.refresh_expires_in !== null ? now + wire.refresh_expires_in * 1000 : null,
    rememberMe: false,
  } satisfies AuthSession)
  return { user, expiresAt: new Date(expiresAtMs).toISOString() }
}

export function useAcceptInviteMutation(): {
  mutateAsync: (input: AcceptInviteInput) => Promise<Session>
  isPending: boolean
  error: AcceptInviteError | null
  reset: () => void
} {
  const queryClient = useQueryClient()
  const mutation = useMutation<Session, AcceptInviteError, AcceptInviteInput>({
    mutationFn: async (input) => {
      try {
        return await acceptInviteRequest(input)
      } catch (cause) {
        throw toAcceptInviteError(cause)
      }
    },
    onSuccess: () => {
      queryClient.clear()
    },
  })
  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  }
}

// ── Forgot password ────────────────────────────────────────────────
//
// The backend returns the same body whether the address is registered or not,
// so the caller learns nothing from the response. The rate-limit surfaces as
// a 429 which we translate into a friendly countdown for the banner — this is
// the only place the endpoint is chatty.

interface ForgotPasswordWire {
  email: string
}

export type ForgotPasswordErrorKind = "rate_limited" | "network" | "server" | "unknown"

export class ForgotPasswordError extends Error {
  readonly kind: ForgotPasswordErrorKind
  readonly retryAfterSeconds: number | null
  constructor(kind: ForgotPasswordErrorKind, message: string, retryAfterSeconds: number | null = null) {
    super(message)
    this.name = "ForgotPasswordError"
    this.kind = kind
    this.retryAfterSeconds = retryAfterSeconds
  }
}

function toForgotPasswordError(cause: unknown): ForgotPasswordError {
  if (cause instanceof ApiError) {
    if (cause.status === 429) {
      return new ForgotPasswordError(
        "rate_limited",
        "Too many attempts. Try again shortly.",
        cause.retryAfterSeconds,
      )
    }
    if (cause.isServer) return new ForgotPasswordError("server", "Server error. Try again shortly.")
    if (cause.status === 0)
      return new ForgotPasswordError("network", "We couldn't reach the server.")
  }
  return new ForgotPasswordError("unknown", "Something went wrong. Please try again.")
}

async function forgotPasswordRequest(email: string): Promise<void> {
  await http.post<{ ok: boolean; detail: string }, ForgotPasswordWire>(
    "/api/v1/auth/forgot-password",
    { email: email.trim() },
  )
}

export function useForgotPasswordMutation(): {
  mutateAsync: (email: string) => Promise<void>
  isPending: boolean
  isSuccess: boolean
  error: ForgotPasswordError | null
  reset: () => void
} {
  const mutation = useMutation<void, ForgotPasswordError, string>({
    mutationFn: async (email) => {
      try {
        await forgotPasswordRequest(email)
      } catch (cause) {
        throw toForgotPasswordError(cause)
      }
    },
  })
  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
    reset: mutation.reset,
  }
}

// ── Reset password ─────────────────────────────────────────────────
//
// On success the backend hands back a full session (see accept-invite for the
// same pattern) so the reviewer lands signed in rather than bouncing to /login
// immediately after proving they own the mailbox.

export interface ResetPasswordInput {
  token: string
  newPassword: string
}

interface ResetPasswordWire {
  token: string
  new_password: string
}

export type ResetPasswordErrorKind = "invalid_or_expired" | "network" | "server" | "unknown"

export class ResetPasswordError extends Error {
  readonly kind: ResetPasswordErrorKind
  constructor(kind: ResetPasswordErrorKind, message: string) {
    super(message)
    this.name = "ResetPasswordError"
    this.kind = kind
  }
}

function toResetPasswordError(cause: unknown): ResetPasswordError {
  if (cause instanceof ApiError) {
    if (cause.status === 400) {
      return new ResetPasswordError(
        "invalid_or_expired",
        "This reset link is invalid or has expired.",
      )
    }
    if (cause.isServer) return new ResetPasswordError("server", "Server error. Try again shortly.")
    if (cause.status === 0)
      return new ResetPasswordError("network", "We couldn't reach the server.")
  }
  return new ResetPasswordError("unknown", "Something went wrong. Please try again.")
}

async function resetPasswordRequest(input: ResetPasswordInput): Promise<Session> {
  const wire = await http.post<TokenResponseWire, ResetPasswordWire>("/api/v1/auth/reset-password", {
    token: input.token,
    new_password: input.newPassword,
  })
  const user = toUser(wire.user)
  const now = Date.now()
  const expiresAtMs = now + wire.expires_in * 1000
  writeAuthSession({
    token: wire.access_token,
    user: toStored(user),
    expiresAt: expiresAtMs,
    refreshToken: wire.refresh_token,
    refreshExpiresAt:
      wire.refresh_expires_in !== null ? now + wire.refresh_expires_in * 1000 : null,
    rememberMe: false,
  } satisfies AuthSession)
  return { user, expiresAt: new Date(expiresAtMs).toISOString() }
}

export function useResetPasswordMutation(): {
  mutateAsync: (input: ResetPasswordInput) => Promise<Session>
  isPending: boolean
  error: ResetPasswordError | null
  reset: () => void
} {
  const queryClient = useQueryClient()
  const mutation = useMutation<Session, ResetPasswordError, ResetPasswordInput>({
    mutationFn: async (input) => {
      try {
        return await resetPasswordRequest(input)
      } catch (cause) {
        throw toResetPasswordError(cause)
      }
    },
    onSuccess: () => {
      queryClient.clear()
    },
  })
  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  }
}

/**
 * The one hook consumers use to answer "who is signed in?". Reads from
 * storage synchronously on first render (no loading flicker on a warm tab),
 * then re-reads on any auth change — same tab or another.
 */
export function useCurrentUser(): User | null {
  const session = React.useSyncExternalStore<AuthSession | null>(
    subscribeAuthSession,
    readAuthSession,
    () => null,
  )
  return React.useMemo(() => {
    if (!session) return null
    const now = Date.now()
    const accessDead = session.expiresAt > 0 && session.expiresAt < now
    const refreshAlive =
      session.refreshToken !== null &&
      (session.refreshExpiresAt === null || session.refreshExpiresAt > now)
    // An expired access token with a live refresh token is not "signed out" —
    // the 401 handler in http.ts will rotate it on the next request. Only
    // when the refresh token is also gone is the session truly dead.
    if (accessDead && !refreshAlive) return null
    return fromStored(session.user)
  }, [session])
}
